import { useMutation, useQueryClient } from "react-query";

import { createTarget } from "api/targets";
import type { Target } from "api/types/aba";
import { useGlobalToast } from "components/GlobalToastProvider";
import { GET_GOAL_BY_ID, GET_LIST_OF_TARGETS } from "@constants/reactQueryKeys";

interface CreateTargetPayload {
  goalId: number;
  targetData: Partial<Target>;
}

/**
 * Create a new target under a goal. Invalidates the target list query for the
 * parent goal on success and surfaces a toast for success/error.
 */
const useCreateTarget = (options: any = {}) => {
  const { onSuccess = () => {}, onError = () => {}, ...otherOptions } = options;
  const queryClient = useQueryClient();
  const { showToast } = useGlobalToast();

  const {
    mutate: createTargetAction,
    isLoading: isCreatingTarget,
    ...rest
  } = useMutation(
    ({ goalId, targetData }: CreateTargetPayload) => createTarget(goalId, targetData),
    {
      onSuccess: (response, variables) => {
        showToast({ message: "Target created successfully" });
        // Refreshes the goal query after target creation
        queryClient.invalidateQueries([GET_LIST_OF_TARGETS, variables.goalId]);
        queryClient.invalidateQueries([GET_GOAL_BY_ID, variables.goalId]);
        onSuccess(response, variables);
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.error || "Failed to create target";
        showToast({ message: errorMessage, errorState: true });
        onError(error);
      },
      ...otherOptions,
    }
  );

  return { createTargetAction, isCreatingTarget, ...rest };
};

export default useCreateTarget;
